import { createElement, useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { Search, Menu, Mic, Newspaper, Mail, Home, CalendarDays, Users, Briefcase, Files, Pin } from 'lucide-react';
import type { ReactNode } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { uiRecoveryHref } from '@/lib/uiExperience';
import { smartTextMatches } from '@shared/search/smartSearch';
import { openQuickNoteComposer } from '@/lib/noteEntityContext';

interface NavigationItem { label: string; path: string; icon: ReactNode; badge?: number; counters?: { managed: number; urgent: number } }
const primary = [
  { label: 'Feed', path: '/', icon: Newspaper },
  { label: 'Email', path: '/emails', icon: Mail },
  { label: 'Dashboard', path: '/dashboard', icon: Home },
  { label: 'Agenda', path: '/calendar', icon: CalendarDays },
  { label: 'Relazioni', path: '/contacts', icon: Users },
  { label: 'Opportunità', path: '/deals', icon: Briefcase },
  { label: 'File', path: '/files', icon: Files },
];

export const DEFAULT_FJORD_PINNED_PATHS = primary.map(item => item.path);
const MAX_FJORD_PINNED_PATHS = 12;

function uniquePaths(paths: string[], available: NavigationItem[]) {
  const availablePaths = new Set(available.map(item => item.path));
  const result: string[] = [];
  for (const path of paths) {
    if (path === '/' || !availablePaths.has(path) || result.includes(path)) continue;
    result.push(path);
  }
  return ['/', ...result].slice(0, MAX_FJORD_PINNED_PATHS);
}

export function resolveFjordPinnedItems(items: NavigationItem[], preferredPaths?: string[]) {
  const all: NavigationItem[] = [...items, { label: 'File', path: '/files', icon: createElement(Files) }]
    .filter((item, i, list) => list.findIndex(other => other.path === item.path) === i);
  const paths = uniquePaths(
    preferredPaths === undefined ? DEFAULT_FJORD_PINNED_PATHS : preferredPaths,
    all,
  );
  const byPath = new Map(all.map(item => [item.path, item]));
  return paths.map(path => byPath.get(path)).filter((item): item is NavigationItem => Boolean(item));
}

export function FjordNavigation({
  items,
  status,
  onLogout,
  preferredPaths,
  onPreferredPathsChange,
}: {
  items: NavigationItem[];
  status: ReactNode;
  onLogout: () => void;
  preferredPaths?: string[];
  onPreferredPathsChange?: (paths: string[]) => void;
}) {
  const [path] = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(false);
  const [localPreferredPaths, setLocalPreferredPaths] = useState<string[] | undefined>(preferredPaths);
  useEffect(() => { setOpen(false); }, [path]);
  useEffect(() => { setLocalPreferredPaths(preferredPaths); }, [preferredPaths]);
  const active = (href: string) => href === '/' ? path === '/' || path === '/feed' : path === href || path.startsWith(href + '/');
  // Consume the existing menu inventory, including future entries; never discard an unknown route.
  const all: NavigationItem[] = [...items, { label: 'File', path: '/files', icon: createElement(Files) }].filter((item, i, list) => list.findIndex(other => other.path === item.path) === i);
  const filtered = all.filter(item => smartTextMatches(item.label, query));
  const pinnedItems = resolveFjordPinnedItems(all, localPreferredPaths);
  const pinnedPaths = new Set(pinnedItems.map(item => item.path));
  const togglePinned = (itemPath: string) => {
    if (itemPath === '/') return;
    const current = pinnedItems.map(item => item.path);
    const next = current.includes(itemPath)
      ? current.filter(pathValue => pathValue !== itemPath)
      : [...current, itemPath];
    setLocalPreferredPaths(next);
    onPreferredPathsChange?.(next);
  };
  const menu = <button type="button" onClick={() => { setQuery(''); setEditing(false); setOpen(true); }} aria-haspopup="dialog" aria-expanded={open}><Menu /><span>Tutti i moduli</span></button>;
  return <>
    <nav className="fjord-navigation" aria-label="Navigazione principale">
      {pinnedItems.map(({ label, path: href, icon }) => <Link key={href} href={href} aria-current={active(href) ? 'page' : undefined}>{icon}<span>{label}</span></Link>)}
      {menu}
    </nav>
    <nav className="fjord-mobile-navigation" aria-label="Navigazione mobile">
      {pinnedItems.slice(0, 4).map(({ label, path: href, icon }) => <Link key={href} href={href} aria-current={active(href) ? 'page' : undefined}>{icon}<span>{label}</span></Link>)}
      <button type="button" onClick={() => openQuickNoteComposer({ startVoice: true })} aria-label="Detta una nota rapida"><Mic aria-hidden="true" /><span>Detta nota</span></button>
      <button type="button" onClick={() => { setQuery(''); setOpen(true); }} aria-haspopup="dialog" aria-expanded={open}><Menu /><span>Menu</span></button>
    </nav>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="fjord-module-dialog">
        <DialogHeader>
          <div className="fjord-module-dialog-heading">
            <div>
              <DialogTitle>Tutti i moduli</DialogTitle>
              <p className="fjord-module-dialog-caption">Il catalogo completo resta sempre disponibile. Feed, notifiche e priorità non vengono nascosti dalla personalizzazione.</p>
            </div>
            <button type="button" className="fjord-module-edit" onClick={() => setEditing(value => !value)}>{editing ? 'Fine' : 'Modifica home'}</button>
          </div>
        </DialogHeader>
        <label className="fjord-module-search"><Search /><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Cerca un modulo…" aria-label="Cerca un modulo" /></label>
        <div className="fjord-module-list">
          {filtered.map(item => <div key={item.path} className="fjord-module-row">
            <Link href={item.path} onClick={() => setOpen(false)} aria-current={active(item.path) ? 'page' : undefined}>
              {item.icon}<span>{item.label}</span>
              {item.counters && <small>{item.counters.managed} gestiti · {item.counters.urgent} urgenti</small>}
              {item.badge !== undefined && <small>{item.badge}</small>}
            </Link>
            {editing && <button
              type="button"
              className={pinnedPaths.has(item.path) ? 'fjord-module-pin fjord-module-pin--active' : 'fjord-module-pin'}
              onClick={() => togglePinned(item.path)}
              aria-pressed={pinnedPaths.has(item.path)}
              aria-label={pinnedPaths.has(item.path) ? `Rimuovi ${item.label} dalla home` : `Mostra ${item.label} nella home`}
              title={pinnedPaths.has(item.path) ? 'Rimuovi dalla barra principale' : 'Mostra nella barra principale'}
            ><Pin aria-hidden="true" />{pinnedPaths.has(item.path) ? 'In home' : 'Mostra'}</button>}
          </div>)}
          {!filtered.length && <p>Nessun modulo trovato.</p>}
        </div>
        <details className="fjord-service-status"><summary>Stato dei servizi</summary>{status}</details>
        <div className="fjord-module-footer"><a href={uiRecoveryHref()}>Ripristina interfaccia precedente</a><button type="button" onClick={onLogout}>Esci</button></div>
      </DialogContent>
    </Dialog>
  </>;
}
